import { getExplodingPositionDeltas } from '@Components/BigBrowse/BBCalculationsUtils';
import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import {
  getNumOfColumnsLarge,
  isLargerThanBreakPoint,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/columnCalculator';
import type { ViewData } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.data';
import {
  FIGMA_COLUMN_FULL_WIDTH,
  FIGMA_COLUMN_GAP_FULL_WIDTH,
  FIGMA_TOP_PADDING_FULL_WIDTH,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.figma';
import { getHeroColumnIndexes } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/header/headerFullWidth/helpers/headerHero.fullWidth';
import { isEven } from '@Utils/miscUtils';
import { Dimensions } from 'react-native';

import type {
  CameraProps,
  PlaneSize,
  PlaneTypes,
  Position2D,
} from '../../../../../Model/ModelTypes';

type GetWorldPositionParams = {
  camera: CameraProps;
  layoutData: Pick<
    ViewData,
    'columnGap' | 'columnWidth' | 'layoutTable' | 'layoutWidth'
  >;
  index: number;
  side: number;
};

function getWorldPosition({
  camera,
  layoutData,
  index,
  side,
}: GetWorldPositionParams) {
  // the camera box on the brand concession will be positioned on 0,0, and it will overlap
  // the current camera box. So the need to target position of the hero will be
  // the position on brand-concession box (0,0 position) but shifted to floor camera box (camera.position.x, camera.position.y)
  // Anchor points are the center of items, and coordinates are x => right, y => up

  // all calculations below consider that BrandConcession/ProductSet canvas is always on zoom = 1.
  // but distances should be applied with the floor canvas which is in a different zoom level.

  // positioned right below the top with a padding
  const y =
    camera.box.top + (-FIGMA_TOP_PADDING_FULL_WIDTH - side / 2) / camera.zoom;

  const x =
    camera.position.x - // from the center of the screen
    layoutData.layoutWidth! / 2 / camera.zoom + // move the left to the edge of grid
    (index * FIGMA_COLUMN_FULL_WIDTH) / camera.zoom +
    (index * FIGMA_COLUMN_GAP_FULL_WIDTH) / camera.zoom + // move right to left of the proper column
    side / 2 / camera.zoom + // move right to half item width, because we anchor on the center
    (isEven(layoutData.layoutTable!.length)
      ? 0
      : -(layoutData.columnWidth! / 2) / camera.zoom); // if columns are odd we shift a bit to the left to cover alignment of the center column

  return { x, y };
}

function getExplodingPositionForHero(
  camera: CameraProps,
  hero: GridCard<PlaneTypes>
) {
  const deltas = getExplodingPositionDeltas(camera.box, hero.box);
  // for some reason deltas are always negative, so we check the quadrant of the hero inside camera box
  // and produce the correct sign for the deltas, so it explodes out to its closets edge
  const dxSign = camera.position.x >= hero.worldPosition.x ? -1 : 1;
  const dySign = camera.position.y >= hero.worldPosition.y ? -1 : 1;

  // 1. With Math.abs we ignore the sign of the "deltas" and apply our own.
  // 2. Because "getExplodingPositionDeltas" was original made for product cards (which are smaller)
  // we add the hero size to the delta to make sure that it really explodes out of the camera box
  return {
    worldPosition: {
      x:
        hero.worldPosition.x + (Math.abs(deltas.dx) + hero.size.width) * dxSign,
      y:
        hero.worldPosition.y +
        (Math.abs(deltas.dy) + hero.size.height) * dySign,
    },
    size: hero.size,
  };
}

export function getMultiSectionMainHeroSpecs(
  camera: CameraProps,
  hero: GridCard<PlaneTypes>
): {
  worldPosition: Position2D;
  size: PlaneSize;
} {
  const isLargerThanBreakpoint = isLargerThanBreakPoint(camera.size.width);

  if (isLargerThanBreakpoint) {
    const layoutData = getNumOfColumnsLarge(camera.size.width);

    // when width is larger than breakpoint, and columns >=4 then target hero has a static position, regardless of the brand-concession header content
    if (layoutData.layoutTable!.length >= 4) {
      // if we are on 4 columns and above, we target the hero on the right of header
      return getHeroSpecsForLargeScreen(layoutData, camera);
    }
  }

  return getExplodingPositionForHero(camera, hero);
}

export const isUltraSmallScreen = (): boolean =>
  Dimensions.get('window').width <= 589;

function getHeroSpecsForLargeScreen(
  layoutData: Pick<
    ViewData,
    'columnGap' | 'columnWidth' | 'layoutTable' | 'layoutWidth'
  >,
  camera: CameraProps
) {
  const side = FIGMA_COLUMN_FULL_WIDTH * 2 + FIGMA_COLUMN_GAP_FULL_WIDTH;

  const [index] = getHeroColumnIndexes(layoutData.layoutTable!.length);
  const worldPosition = getWorldPosition({ camera, layoutData, index, side });

  return {
    worldPosition,

    size: { width: side / camera.zoom, height: side / camera.zoom },
  };
}
